/**
 * routes.ts — the route table, keyed by instance mode.
 *
 * decision-2026-07-25-broker-mode-split (option C + B): the operator provisioning
 * surface is gated on MODE, structurally — not on roles. Before the split every
 * mode shared one implicit route table and `/admin/sandbox-provisioning` was
 * reachable on any deployment that authenticated, with `hasRole()` as the only
 * thing standing between a tenant user and Provision / Promote / Decommission.
 *
 * Registration happens here and nowhere else:
 *   • AppShell renders a route only if `isRouteRegistered(mode, route)`.
 *   • AdminMenu and NavRail derive their items from this table.
 *   • An unregistered route resolves to NOT FOUND — not to a role prompt. A role
 *     prompt would tell a tenant user the capability exists and they lack the role;
 *     the capability does not exist on that deployment at all (adr-075 D11).
 *
 * Role checks stay in place on top of this, as defense in depth. Mode decides what
 * EXISTS; roles decide who may use what exists.
 *
 * Restated gate 9(a) tests against this module directly.
 *
 * decision-2026-08-01-vertical-broker-mode-split: added the 'vertical-broker' case
 * (ADR-075's vertical/management-plane broker, e.g. dev.deal.do.place) — same common
 * surfaces as meta, deliberately excluded from OPERATOR_ROUTES below.
 *
 * decision-2026-08-02-tenant-broker-is-user-oriented (Stephan, watched UAT session):
 * the tenant broker is a USER surface — users are never presented application-
 * management tasks; the single exception is the uat-approver decision panel, which
 * lives in the service detail overlay, not behind a route. The tenant table is
 * therefore the TENANT STORE ONLY: the /admin group (and with it the App Operations
 * Board, which rides on /admin via the dynamic pattern) and /sandbox/request are
 * absent by MODE — the observed "Administration → requires elevated access" and a
 * role-blocked Sandbox page violated the absent-not-denied principle above. Sandbox
 * requests live on the shared VERTICAL broker per the 2026-08-01 SaaS amendment
 * (a tenant has no own broker instance; dev.mtcm.do.place is affected identically).
 *
 * ruling-2026-08-02-tenant-store-has-no-dashboard: /dashboard left the tenant
 * table for the same reason - see TENANT_STORE_ROUTES below.
 *
 * ruling-2026-08-02-rail-absent-not-denied: mode registration is only the first
 * filter. NavRail applies a ROLE predicate on top of this table, so a route this
 * mode registers but this identity cannot use is absent from the rail rather
 * than offered-then-denied. That predicate lives in NavRail.tsx, deliberately
 * NOT here: this table answers "what exists on this deployment", which is a
 * property of the deployment and not of the caller.
 */

import type { InstanceMode } from '../config/schema';
import { META_MODE, TENANT_MODE, VERTICAL_BROKER_MODE } from '../config/schema';
import type { Route } from './Router';

/** Routes every authenticated MANAGEMENT-PLANE broker carries (meta, vertical). */
const COMMON_BROKER_ROUTES: Route[] = [
  '/',
  '/dashboard',
  '/profile',
  '/cart',
  '/admin',
  '/admin/sub-clients',
  '/admin/subscriptions',
  '/admin/orders',
  '/admin/audit-log',
];

/**
 * The operator provisioning surface. Meta broker only — this is the whole point of
 * the split. Do not add it to any other mode's table without a new decision.
 */
export const OPERATOR_ROUTES: Route[] = ['/admin/sandbox-provisioning'];

/** The tenant-side sandbox request surface. Lives on the SHARED VERTICAL broker
 * (2026-08-01 SaaS amendment) — not on tenant stores. */
export const TENANT_ROUTES: Route[] = ['/sandbox/request'];

/**
 * The tenant STORE (user surface) — decision-2026-08-02-tenant-broker-is-user-
 * oriented. Storefront only: no /admin group, no operations board, no sandbox
 * request. What a signed-in store user gets, and nothing a store user cannot use.
 *
 * ruling-2026-08-02-tenant-store-has-no-dashboard: /dashboard is gone from this
 * table. It renders DashboardPage - the BROKER-OPERATIONS dashboard, i.e. a
 * management capability - so leaving it registered meant the store's own rail
 * offered it and a store user landed on "Your account needs operator or
 * administrative privileges". Same absent-not-denied principle as the /admin
 * group above; the ruling applies it to the one route that had slipped through.
 * The store's default surface is the catalogue ('/'), which is what a store IS.
 * A user-scoped tenant dashboard, if ever wanted, is a NEW surface and a new
 * decision - not a re-registration of this route.
 */
const TENANT_STORE_ROUTES: Route[] = ['/', '/profile', '/cart'];

/** Minimal surfaces for the pre-split, non-authenticated modes — unchanged. */
const TIER0_ROUTES: Route[] = ['/', '/dashboard', '/profile'];
const REFERENCE_ROUTES: Route[] = ['/'];

export function routesForMode(mode: InstanceMode): Route[] {
  switch (mode) {
    case META_MODE:
      return [...COMMON_BROKER_ROUTES, ...OPERATOR_ROUTES];
    case TENANT_MODE:
      // decision-2026-08-02-tenant-broker-is-user-oriented — see file header.
      return [...TENANT_STORE_ROUTES];
    case VERTICAL_BROKER_MODE:
      // decision-2026-08-01-vertical-broker-mode-split (amended 2026-08-01 for the
      // SaaS model): the vertical's own multi-tenant management-plane broker. Same
      // common surfaces as meta, NO operator routes — BUT it carries the tenant
      // sandbox-request surface, because in the SaaS product a tenant does not get
      // their own vertical/broker instance; they request and manage apps here, on
      // the shared vertical broker (tenant scoping comes from the token, not the
      // instance). See decision amendment 2026-08-01-vertical-broker-carries-sandbox.
      return [...COMMON_BROKER_ROUTES, ...TENANT_ROUTES];
    case 'tier-1-hub':
      return [...COMMON_BROKER_ROUTES];
    case 'tier-0':
      return TIER0_ROUTES;
    case 'reference':
      return REFERENCE_ROUTES;
    default:
      return REFERENCE_ROUTES;
  }
}

// ── Surface planes (ADR-075 vocabulary) ──────────────────────────────────────

/**
 * ADR-075's own two words for what a deployment IS. Deliberately NOT a new
 * abstraction: "management plane" and "consumption plane" are the ADR's terms,
 * and this is the single place a mode becomes one of them.
 *
 * decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench retired
 * `isAdminCatalog` — one boolean that conflated two independent questions:
 * WHAT KIND OF DEPLOYMENT IS THIS (a property of the instance) and WHAT MAY
 * THIS CALLER DO (a property of the token). The plane answers only the first.
 * It owns CARD SHAPE and nothing about roles; roles live in auth/surfaceAccess.
 *
 *   management  — the meta broker (vertical-ref), the vertical broker
 *                 (vertical-broker) and legacy tier-1-hub. Lifecycle badge, no
 *                 commerce CTA, no plans/pricing modal, no muting.
 *   consumption — the tenant broker (vertical-tenant). Commerce CTA, View
 *                 details, plans/pricing modal, muting.
 *
 * tier-0 and reference are pre-split, non-authenticated storefronts. They
 * render the consumption shape today and keep it via the default branch, so
 * this function changes nothing for them.
 *
 * Same table as routesForMode above, same file, on purpose: a mode added to one
 * and forgotten in the other is the class of bug that put OPERATOR_ROUTES on
 * dev.deal.do.place.
 */
export type SurfacePlane = 'management' | 'consumption';

export function planeForMode(mode: InstanceMode): SurfacePlane {
  switch (mode) {
    case META_MODE:
    case VERTICAL_BROKER_MODE:
    case 'tier-1-hub':
      return 'management';
    case TENANT_MODE:
      return 'consumption';
    default:
      return 'consumption';
  }
}

/** Convenience for the many call sites that only ask "is this a management plane?". */
export function isManagementPlane(mode: InstanceMode): boolean {
  return planeForMode(mode) === 'management';
}

/**
 * Dynamic-segment routes are matched by shape, not listed literally. They ride on
 * their parent surface's registration — so the App Operations Board
 * (/apps/:id/operations) exists exactly where /admin exists: meta and vertical
 * broker, never the tenant store.
 */
const DYNAMIC_ROUTE_PATTERNS: { re: RegExp; requires: Route }[] = [
  { re: /^\/admin\/sub-clients\/[^/]+$/, requires: '/admin/sub-clients' },
  { re: /^\/apps\/[a-z0-9-]+\/operations$/, requires: '/admin' },
];

export function isRouteRegistered(mode: InstanceMode, route: string): boolean {
  const table = routesForMode(mode);
  if ((table as string[]).includes(route)) return true;
  for (const { re, requires } of DYNAMIC_ROUTE_PATTERNS) {
    if (re.test(route) && (table as string[]).includes(requires)) return true;
  }
  return false;
}

/** Convenience for the surfaces that ask "does this deployment provision at all?". */
export function hasOperatorProvisioning(mode: InstanceMode): boolean {
  return routesForMode(mode).includes(OPERATOR_ROUTES[0]);
}
